import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useExamTimer — the countdown the candidate sees.
 *
 * ⚠️ This is DECORATION. The server owns the clock: `expiresAt` is set once at
 * start, and every save/heartbeat/submit re-derives remaining time from it. This
 * hook exists so the number on screen moves smoothly between those round-trips,
 * and it SNAPS to the server whenever the server tells it something different.
 *
 * Two decisions worth knowing:
 *
 *   - It counts down to a DEADLINE, it does not subtract one per tick. A browser
 *     throttles timers in a background tab to once a minute, and a laptop lid
 *     stops them entirely; a decrementing clock comes back minutes behind. This
 *     one comes back right, because it asks "how far is the deadline" each time.
 *     It never pauses and never adds time.
 *
 *   - The ticking number lives in a tiny store, not in React state. The exam
 *     screen re-renders when the URGENCY changes (three times a sitting), and
 *     only <ExamClock> re-renders each second. Re-rendering a 100-question
 *     palette and a page of typeset maths every second is what made the old
 *     screen stutter on low-end phones.
 *
 * Milestones fire once each and are non-blocking: a modal at 00:59 costs a
 * candidate their last question.
 */

const MILESTONES = [
  { key: "half", at: (total) => Math.floor(total / 2), label: "Half your time is left" },
  { key: "10m", at: () => 600, label: "10 minutes left" },
  { key: "5m", at: () => 300, label: "5 minutes left" },
  { key: "1m", at: () => 60, label: "1 minute left" },
];

// Below this, a difference between our deadline and the server's answer is just
// latency, and moving the number for it would make it jitter.
const DRIFT_TOLERANCE_SECONDS = 5;

// Checked four times a second so a late timer never skips a displayed second;
// listeners are only told when the whole second actually changes.
const TICK_MS = 250;

export function formatTime(seconds) {
  if (seconds == null) return "--:--";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/** Spoken form for the screen-reader label — "12 minutes 5 seconds remaining". */
export function speakTime(seconds) {
  if (seconds == null) return "No time limit";
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec} second${sec === 1 ? "" : "s"} remaining`;
  return `${m} minute${m === 1 ? "" : "s"} ${sec} second${sec === 1 ? "" : "s"} remaining`;
}

export const urgencyOf = (seconds) => {
  if (seconds == null) return "none";
  if (seconds <= 60) return "critical";
  if (seconds <= 300) return "warning";
  return "normal";
};

/** The smallest store useSyncExternalStore can read. */
function createClock() {
  let value = null;
  const listeners = new Set();
  return {
    get: () => value,
    set: (next) => {
      if (next === value) return;
      value = next;
      listeners.forEach((l) => l());
    },
    // Adopting the first value happens during render, before anyone listens.
    init: (next) => { value = next; },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export default function useExamTimer({ initialSeconds, onExpire, onMilestone }) {
  const clockRef = useRef(null);
  if (!clockRef.current) clockRef.current = createClock();
  const clock = clockRef.current;

  const deadlineRef = useRef(null); // epoch ms
  const totalRef = useRef(0);
  const firedRef = useRef(new Set());
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  const onMilestoneRef = useRef(onMilestone);

  // Keep the callbacks fresh without restarting the interval.
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);
  useEffect(() => { onMilestoneRef.current = onMilestone; }, [onMilestone]);

  // The hook mounts BEFORE the attempt is loaded. Adopt the first real number
  // the loader hands over — here, during render, so the first frame already
  // shows the right time rather than flashing "No limit".
  //
  // ⚠️ Only once: re-adopting would rewind a running clock to the loaded value.
  // After this, nothing but reconcile() (the server's answer) moves the deadline.
  if (deadlineRef.current === null && initialSeconds != null) {
    const start = Math.max(0, Math.floor(initialSeconds));
    deadlineRef.current = Date.now() + start * 1000;
    totalRef.current = start;
    clock.init(start);
  }

  const [urgency, setUrgency] = useState(() => urgencyOf(clock.get()));
  const hasLimit = deadlineRef.current !== null;

  const tick = useCallback(() => {
    if (deadlineRef.current === null) return;
    const prev = clock.get();
    const next = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
    if (next === prev) return;

    if (prev !== null) {
      for (const m of MILESTONES) {
        const at = m.at(totalRef.current);
        if (at > 0 && prev > at && next <= at && !firedRef.current.has(m.key)) {
          firedRef.current.add(m.key);
          onMilestoneRef.current?.(m);
        }
      }
    }

    clock.set(next);
    setUrgency(urgencyOf(next));

    if (next <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpireRef.current?.();
    }
  }, [clock]);

  useEffect(() => {
    if (!hasLimit) return undefined; // untimed paper
    setUrgency(urgencyOf(clock.get()));
    // A paper that loads with no time left expires on the first tick.
    const id = setInterval(tick, TICK_MS);
    // Coming back to the tab is the moment a throttled timer is most wrong.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasLimit, tick, clock]);

  /**
   * Accept the server's answer. Called from every save/heartbeat response.
   * The server is right, in both directions.
   */
  const reconcile = useCallback((serverSeconds) => {
    if (serverSeconds == null || !Number.isFinite(Number(serverSeconds))) return;
    const server = Math.max(0, Math.floor(Number(serverSeconds)));
    const local = clock.get();
    if (deadlineRef.current !== null && local !== null && Math.abs(local - server) <= DRIFT_TOLERANCE_SECONDS) {
      return;
    }
    deadlineRef.current = Date.now() + server * 1000;
    if (!totalRef.current) totalRef.current = server;
    tick();
    if (server <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpireRef.current?.();
    }
  }, [clock, tick]);

  /**
   * Start over at `seconds` — a timed section that has just opened. Milestones
   * fire again and the clock can expire again; nothing else moves the
   * deadline but reconcile().
   */
  const restart = useCallback((seconds) => {
    if (seconds == null || !Number.isFinite(Number(seconds))) return;
    const s = Math.max(0, Math.floor(Number(seconds)));
    deadlineRef.current = Date.now() + s * 1000;
    totalRef.current = s;
    firedRef.current = new Set();
    expiredRef.current = false;
    clock.set(s);
    setUrgency(urgencyOf(s));
  }, [clock]);

  return {
    /** Subscribe with <ExamClock clock={timer.clock} />. */
    clock,
    hasLimit,
    urgency,
    reconcile,
    restart,
    /** Seconds left right now, for code that needs the number once. */
    getTimeLeft: clock.get,
  };
}
